import { useId } from 'react';
import type { CeoBankAccount, CeoDailyPaymentsSummary } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  formatDateRu,
  formatMillions,
  formatMoney,
  PAYMENT_CATEGORY_LABELS,
} from '../ceoFormat';
import { DailyPaymentsTable } from './DailyPaymentsTable';
import { ManualPaymentForm } from './ManualPaymentForm';
import {
  ActionHint,
  ActionWithHint,
  DailySummarySubtext,
  FilterBar,
  FilterHint,
  FilterLabel,
  HeaderRow,
  InflowTotalText,
  Input,
  OutflowTotalText,
  Section,
  Select,
  Title,
} from './styles';

interface DailyPaymentsSectionProps {
  /** `undefined` — операции выбранного дня ещё загружаются. */
  dailyData: CeoDailyPaymentsSummary | undefined;
  accounts: CeoBankAccount[];
  can_write: boolean;
  showAddPayment: boolean;
  setShowAddPayment: (v: boolean | ((prev: boolean) => boolean)) => void;
  filterDate: string;
  setFilterDate: (v: string) => void;
  filterAccount: string;
  setFilterAccount: (v: string) => void;
  filterDirection: string;
  setFilterDirection: (v: string) => void;
  filterCategory: string;
  setFilterCategory: (v: string) => void;
  onResetFilter: () => void;
}

export function DailyPaymentsSection({
  dailyData,
  accounts,
  can_write,
  showAddPayment,
  setShowAddPayment,
  filterDate,
  setFilterDate,
  filterAccount,
  setFilterAccount,
  filterDirection,
  setFilterDirection,
  filterCategory,
  setFilterCategory,
  onResetFilter,
}: DailyPaymentsSectionProps) {
  const id = useId();
  // Вручную — только по счёту без подключения к банку: по счёту с банком операции приходят
  // из его выписок, ручная запись задвоила бы банковскую.
  const hasManualAccounts = accounts.some((a) => !a.connection_id);

  return (
    <Section>
      <HeaderRow>
        <div>
          <Title>
            {filterDate
              ? `Платежи за ${formatDateRu(filterDate)}`
              : `Прошлый операционный день — ${dailyData ? formatDateRu(dailyData.report_date) : '…'}`}
          </Title>
          {dailyData && (
            <DailySummarySubtext>
              Входящий остаток: <strong>{formatMillions(dailyData.opening_balance)}</strong> ·
              Поступило за день ({dailyData.inflow_count} п/п):{' '}
              <InflowTotalText>
                +{formatMoney(dailyData.inflow_total)}
              </InflowTotalText>{' '}
              · Списано за день ({dailyData.outflow_count} п/п):{' '}
              <OutflowTotalText>
                −{formatMoney(dailyData.outflow_total)}
              </OutflowTotalText>{' '}
              · Исходящий остаток: <strong>{formatMillions(dailyData.closing_balance)}</strong>
              {dailyData.internal_transfer_count > 0 && (
                <span>
                  {' '}
                  · Межсчетные переводы ({dailyData.internal_transfer_count} п/п):{' '}
                  <strong>{formatMoney(dailyData.internal_transfer_total)}</strong>
                </span>
              )}
            </DailySummarySubtext>
          )}
        </div>
        {can_write && (
          <ActionWithHint>
            <Button
              $size="sm"
              $variant="secondary"
              disabled={!hasManualAccounts}
              aria-describedby={hasManualAccounts ? undefined : `${id}-manual-hint`}
              onClick={() => setShowAddPayment((v) => !v)}
            >
              + Добавить операцию вручную
            </Button>
            {!hasManualAccounts && (
              <ActionHint id={`${id}-manual-hint`}>
                Все счета подключены к банку: их операции приходят из выписок банка.
              </ActionHint>
            )}
          </ActionWithHint>
        )}
      </HeaderRow>

      {can_write && showAddPayment && hasManualAccounts && (
        <ManualPaymentForm accounts={accounts} onClose={() => setShowAddPayment(false)} />
      )}

      <FilterBar>
        <FilterLabel htmlFor={`${id}-date`}>Дата платежей</FilterLabel>
        <Input
          id={`${id}-date`}
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          aria-describedby={filterDate ? undefined : `${id}-date-hint`}
        />
        {!filterDate && (
          <FilterHint id={`${id}-date-hint`}>не выбрана — показан прошлый операционный день</FilterHint>
        )}
        <Select
          value={filterAccount}
          onChange={(e) => setFilterAccount(e.target.value)}
          aria-label="Банковский счёт"
        >
          <option value="">Все банковские счета</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.bank_name} · {a.account_number}
            </option>
          ))}
        </Select>
        <Select
          value={filterDirection}
          onChange={(e) => setFilterDirection(e.target.value)}
          aria-label="Приход или расход"
        >
          <option value="">Все операции (приход + расход)</option>
          <option value="inflow">Только поступления (+)</option>
          <option value="outflow">Только списания (−)</option>
        </Select>
        <Select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          aria-label="Категория ДДС"
        >
          <option value="">Все категории ДДС</option>
          {Object.entries(PAYMENT_CATEGORY_LABELS).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </Select>
        {(filterDate || filterAccount || filterDirection || filterCategory) && (
          <Button
            $size="sm"
            $variant="ghost"
            onClick={onResetFilter}
            title="Вернуть прошлый операционный день по всем счетам"
          >
            Сбросить
          </Button>
        )}
      </FilterBar>

      <DailyPaymentsTable payments={dailyData?.payments} />
    </Section>
  );
}
